import { useEffect, useRef } from "react"
import mapboxgl from "mapbox-gl"
import { bboxPolygon, booleanIntersects } from "@turf/turf"
import { useFilterQueryParams, FilterQueryArgs } from "../../../hooks/filter_query_params"
import { FanBboxLayerId } from "./flat_source"
import { useMapData } from "../useMapData"
import { getPointOnPolygon, getSmallestFeature, PolygonPointOption, calculateScreenAnchor, SCREEN_ANCHOR_THRESHOLD } from "../../../../../utils/map"
import eventBus from "../../../../../hooks/eventBus"

/**
 * Offsets in pixels from viewport edges that define "danger zones" where tooltips
 * should flip to avoid overlaying features. Uses the shared SCREEN_ANCHOR_THRESHOLD
 * constant so both polygon and flow tooltip edge detection stay in sync.
 */
const DANGER_ZONE_OFFSETS = {
  top: SCREEN_ANCHOR_THRESHOLD,
  bottom: SCREEN_ANCHOR_THRESHOLD,
  left: SCREEN_ANCHOR_THRESHOLD,
  right: SCREEN_ANCHOR_THRESHOLD,
}

/**
 * @typedef {Object} DefaultMapBehaviorProps
 * @property {mapboxgl.Map|null} map - The Mapbox GL map instance
 * @property {boolean} isMapLoading - Boolean indicating if the map is currently loading
 * @property {Array<string>} layerIds - Array of layer IDs to make interactive for click and hover events
 */

/**
 * Calculates which polygon point to use for tooltip anchoring based on feature geometry
 * and viewport edges. Checks if the feature intersects with danger zones and returns
 * both the polygon point option and the corresponding Mapbox popup anchor.
 *
 * @param {Object} feature - GeoJSON feature to position tooltip for
 * @param {mapboxgl.Map} map - Mapbox map instance
 * @param {Object} mapPadding - Map padding object with {top, bottom, left, right}
 * @returns {{pointOption: number, anchor: string}} Object containing the PolygonPointOption and Mapbox popup anchor string
 */
export function calculatePolygonPointOption(feature, map, mapPadding) {
  // Input validation
  if (!feature || !map || !mapPadding) {
    console.warn(
      "calculatePolygonPointOption: Missing required parameters, defaulting to TOP_CENTER"
    )
    return { pointOption: PolygonPointOption.TOP_CENTER, anchor: "bottom" }
  }

  // Validate feature geometry
  if (!feature.geometry) {
    return { pointOption: PolygonPointOption.TOP_CENTER, anchor: "bottom" }
  }

  // Ensure mapPadding has all required properties
  const padding = {
    top: mapPadding.top || 0,
    bottom: mapPadding.bottom || 0,
    left: mapPadding.left || 0,
    right: mapPadding.right || 0,
  }

  // Get map container bounds in pixels
  const canvas = map.getCanvas()

  // Validate canvas dimensions
  if (!canvas || canvas.width === 0 || canvas.height === 0) {
    console.warn("calculatePolygonPointOption: Invalid canvas dimensions, defaulting to TOP_CENTER")
    return { pointOption: PolygonPointOption.TOP_CENTER, anchor: "bottom" }
  }

  const canvasWidth = canvas.width
  const canvasHeight = canvas.height

  // Calculate danger zone boundaries in pixels (viewport edges + padding + offsets)
  const dangerZones = {
    top: {
      minX: 0,
      minY: 0,
      maxX: canvasWidth,
      maxY: padding.top + DANGER_ZONE_OFFSETS.top,
    },
    bottom: {
      minX: 0,
      minY: canvasHeight - padding.bottom - DANGER_ZONE_OFFSETS.bottom,
      maxX: canvasWidth,
      maxY: canvasHeight,
    },
    left: {
      minX: 0,
      minY: 0,
      maxX: padding.left + DANGER_ZONE_OFFSETS.left,
      maxY: canvasHeight,
    },
    right: {
      minX: canvasWidth - padding.right - DANGER_ZONE_OFFSETS.right,
      minY: 0,
      maxX: canvasWidth,
      maxY: canvasHeight,
    },
  }

  // Convert each danger zone from pixel bounds to geographic bounds
  const dangerZonePolygons = {}
  for (const [edge, bounds] of Object.entries(dangerZones)) {
    const nw = map.unproject([bounds.minX, bounds.minY])
    const se = map.unproject([bounds.maxX, bounds.maxY])

    // Create bbox [minLng, minLat, maxLng, maxLat]
    const bbox = [nw.lng, se.lat, se.lng, nw.lat]
    dangerZonePolygons[edge] = bboxPolygon(bbox)
  }

  // Check for intersections with each danger zone
  try {
    const intersections = {
      bottom: booleanIntersects(feature, dangerZonePolygons.bottom),
      top: booleanIntersects(feature, dangerZonePolygons.top),
      left: booleanIntersects(feature, dangerZonePolygons.left),
      right: booleanIntersects(feature, dangerZonePolygons.right),
    }

    // Choose polygon point based on which danger zones are intersected
    // Corner cases (multiple intersections)
    if (intersections.bottom && intersections.left) {
      return { pointOption: PolygonPointOption.TOP_RIGHT, anchor: "bottom-left" }
    }
    if (intersections.bottom && intersections.right) {
      return { pointOption: PolygonPointOption.TOP_LEFT, anchor: "bottom-right" }
    }
    if (intersections.top && intersections.left) {
      return { pointOption: PolygonPointOption.BOTTOM_RIGHT, anchor: "top-left" }
    }
    if (intersections.top && intersections.right) {
      return { pointOption: PolygonPointOption.BOTTOM_LEFT, anchor: "top-right" }
    }

    // Edge cases (single intersection)
    if (intersections.bottom) {
      return { pointOption: PolygonPointOption.TOP_CENTER, anchor: "bottom" }
    }
    if (intersections.top) {
      return { pointOption: PolygonPointOption.BOTTOM_CENTER, anchor: "top" }
    }
    if (intersections.left) {
      return { pointOption: PolygonPointOption.RIGHT, anchor: "left" }
    }
    if (intersections.right) {
      return { pointOption: PolygonPointOption.LEFT, anchor: "right" }
    }

    // Default: no intersections, use top center
    return { pointOption: PolygonPointOption.TOP_CENTER, anchor: "bottom" }
  } catch (error) {
    console.warn("calculatePolygonPointOption: Error checking intersections", error)
    return { pointOption: PolygonPointOption.TOP_CENTER, anchor: "bottom" }
  }
}

/**
 * Manages default map interaction behavior including click selection and hover popups.
 * Handles feature selection on click, updates the resource ID in URL query params,
 * and displays hover popups with feature information. Automatically manages cursor
 * state and popup lifecycle based on mouse interactions.
 *
 * @param {DefaultMapBehaviorProps} props - The component props
 * @returns {void}
 */
export function DefaultMapBehavior({ map, isMapLoading, layerIds }) {
  const { resourceId, setResourceId, campusFlow, flowBuilding, setFilters } = useFilterQueryParams()
  const { getMapPopup, mapPadding } = useMapData()
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: false, closeOnClick: false }))
  const hoveredFeatureRef = useRef(null)

  useEffect(() => {
    if (!map || isMapLoading) {
      return
    }

    const handleOnClick = e => {
      const setResourceIdWithEvent = (resourceId, feature = null) => {
        setResourceId(resourceId)
        if (resourceId) {
          eventBus.emit("listview-resource-selected", {
            id: resourceId,
            name: feature?.properties?.name || null,
            type: feature?.properties?.subType || null,
            source: "map",
          })
        }
      }

      // Query all features at the click point
      const allFeatures = map.queryRenderedFeatures(e.point)

      // Check if any of the clicked features belong to our interactive layers
      const interactiveFeatures = allFeatures.filter(feature => layerIds.includes(feature.layer.id))

      // Campus flow building selection
      if (campusFlow) {
        if (!interactiveFeatures.length) {
          setFilters({ [FilterQueryArgs.RESOURCE_ID]: null, [FilterQueryArgs.FLOW_BUILDING]: null })
          return
        }

        const buildingFeature = interactiveFeatures.find(f => f.properties?.buildingId)
        if (buildingFeature) {
          const clickedBuildingId = buildingFeature.properties.buildingId
          const clickedZoneId = buildingFeature.properties.id

          if (clickedBuildingId === flowBuilding) {
            // Click same building — deselect
            setFilters({ [FilterQueryArgs.RESOURCE_ID]: null, [FilterQueryArgs.FLOW_BUILDING]: null })
          } else {
            // Click different building — select it
            setFilters({ [FilterQueryArgs.RESOURCE_ID]: clickedZoneId, [FilterQueryArgs.FLOW_BUILDING]: clickedBuildingId })
            eventBus.emit("listview-resource-selected", {
              id: clickedZoneId,
              name: buildingFeature.properties?.name || null,
              type: buildingFeature.properties?.subType || null,
              source: "map",
            })
          }
          return
        }
        // Non-building feature clicked in campus flow mode — clear flow selection
        setFilters({ [FilterQueryArgs.RESOURCE_ID]: null, [FilterQueryArgs.FLOW_BUILDING]: null })
        return
      }

      if (!interactiveFeatures.length) {
        // No interactive layer was clicked - clear selection
        setResourceIdWithEvent(null)
        return
      }

      // Check if bbox is present - this means we're in building view mode (flyover is showing)
      const hasBbox = interactiveFeatures.some(f => f.layer.id === FanBboxLayerId)

      if (hasBbox) {
        // In building view mode - only allow fan layer interactions
        const fanFeatures = interactiveFeatures.filter(
          f => f.layer.id.includes("-fan-") && f.layer.id !== FanBboxLayerId
        )

        if (fanFeatures.length === 0) {
          // Clicking on bbox or base layers - swallow the click
          return
        }

        // Click on fan layer feature - select it
        if (fanFeatures.length === 1) {
          const clickedId = fanFeatures[0].properties.id
          // Don't deselect if clicking the same zone
          if (clickedId !== resourceId) {
            setResourceIdWithEvent(clickedId, fanFeatures[0])
          }
          return
        }

        const { polygon } = getSmallestFeature(fanFeatures)
        if (polygon) {
          const clickedId = polygon.properties.id
          // Don't deselect if clicking the same zone
          if (clickedId !== resourceId) {
            setResourceIdWithEvent(clickedId, polygon)
          }
        }
        return
      }

      // Not in building view mode - normal selection behavior
      if (interactiveFeatures.length === 1) {
        const clickedId = interactiveFeatures[0].properties.id
        // Don't deselect if clicking the same zone
        if (clickedId !== resourceId) {
          setResourceIdWithEvent(clickedId, interactiveFeatures[0])
        }
        return
      }

      const { polygon } = getSmallestFeature(interactiveFeatures)
      if (polygon) {
        const clickedId = polygon.properties.id
        // Don't deselect if clicking the same zone
        if (clickedId !== resourceId) {
          setResourceIdWithEvent(clickedId, polygon)
        }
      }
    }

    const handleOnMouseMove = e => {
      // --- Phase 1: Query features ---
      // Query with bbox for line features (thin, need tolerance).
      // Filter by exact source ID to only match flow lines, excluding
      // basemap roads, zone outlines, and other da-* line layers.
      const FLOW_BBOX_SIZE = 10
      const bbox = [
        [e.point.x - FLOW_BBOX_SIZE, e.point.y - FLOW_BBOX_SIZE],
        [e.point.x + FLOW_BBOX_SIZE, e.point.y + FLOW_BBOX_SIZE],
      ]
      const bboxFeatures = map.queryRenderedFeatures(bbox)
      const lineFeatures = bboxFeatures.filter(
        f => f.layer.type === "line" && f.source === "da-campus-flow-source"
      )

      // Query point/polygon features at exact cursor position
      const allFeatures = map.queryRenderedFeatures(e.point)
      const interactiveFeatures = allFeatures.filter(feature => layerIds.includes(feature.layer.id))

      // --- Phase 2: Feature selection with priority ---
      let currentFeature = null
      let isLineFeature = false

      // Priority 1: Line features (from bbox query)
      if (lineFeatures.length > 0) {
        // Deduplicate (same route can appear from multiple line segments)
        const seen = new Set()
        const unique = lineFeatures.filter(f => {
          const id = f.properties.id
          if (seen.has(id)) return false
          seen.add(id)
          return true
        })
        // Store all unique features for multi-route popup
        currentFeature = unique.length === 1 ? unique[0] : unique
        isLineFeature = true
      }

      // Priority 2: Point/Polygon features (existing logic)
      if (!currentFeature && interactiveFeatures.length > 0) {
        const hasBbox = interactiveFeatures.some(f => f.layer.id === FanBboxLayerId)

        if (hasBbox) {
          const fanFeatures = interactiveFeatures.filter(
            f => f.layer.id.includes("-fan-") && f.layer.id !== FanBboxLayerId
          )
          if (fanFeatures.length > 0) {
            const { point, polygon } = getSmallestFeature(fanFeatures)
            currentFeature = point || polygon
          }
        } else {
          const { point, polygon } = getSmallestFeature(interactiveFeatures)
          currentFeature = point || polygon
        }
      }

      // --- Phase 3: Dedup check (same feature as before?) ---
      // Sort IDs for stable key regardless of queryRenderedFeatures order
      const currentFeatureId = Array.isArray(currentFeature)
        ? currentFeature.map(f => f.properties.id).sort().join(",")
        : currentFeature?.properties?.id || null
      const previousFeatureId = hoveredFeatureRef.current?.properties?.id || null

      if (currentFeatureId === previousFeatureId) {
        return
      }

      hoveredFeatureRef.current = Array.isArray(currentFeature)
        ? { properties: { id: currentFeatureId } }
        : currentFeature

      if (!currentFeature) {
        map.getCanvas().style.cursor = ""
        popupRef.current.remove()
        return
      }

      // --- Phase 4: Popup display ---
      map.getCanvas().style.cursor = "pointer"

      // Remove existing popup before re-adding with new options.
      // Mapbox GL applies className during container creation, so mutating
      // options.className without remove+addTo won't update the DOM.
      popupRef.current.remove()

      const popupContent = getMapPopup(currentFeature)
      let coordinates = null

      if (isLineFeature) {
        // Line features: anchor at cursor position with edge-aware flipping.
        // Use clientWidth/clientHeight (CSS pixels) to match e.point coordinate space,
        // NOT canvas.width/height which reports device pixels on HiDPI displays.
        coordinates = [e.lngLat.lng, e.lngLat.lat]
        const canvas = map.getCanvas()
        popupRef.current.options.anchor = calculateScreenAnchor(
          e.point, canvas.clientWidth, canvas.clientHeight, mapPadding
        )
        popupRef.current.options.maxWidth = "none"
        popupRef.current.options.className = "flow-popup-container"
      } else if (currentFeature.geometry.type === "Point") {
        coordinates = currentFeature.geometry.coordinates
        popupRef.current.options.anchor = "top"
        popupRef.current.options.maxWidth = "240px"
        popupRef.current.options.className = ""
      } else {
        // Polygon: use danger zone positioning
        const { pointOption, anchor } = calculatePolygonPointOption(currentFeature, map, mapPadding)
        const popupPoint = getPointOnPolygon(currentFeature, pointOption)
        if (popupPoint) {
          coordinates = popupPoint.geometry.coordinates
          popupRef.current.options.anchor = anchor
          popupRef.current.options.maxWidth = "240px"
          popupRef.current.options.className = ""
        }
      }

      if (coordinates && popupContent) {
        popupRef.current
          .setLngLat(coordinates.slice ? coordinates.slice() : coordinates)
          .setHTML(popupContent)
          .addTo(map)
      }
    }

    // Add general map event handlers
    map.on("click", handleOnClick)
    map.on("mousemove", handleOnMouseMove)

    return () => {
      map.off("click", handleOnClick)
      map.off("mousemove", handleOnMouseMove)
    }
  }, [isMapLoading, layerIds, map, setResourceId, getMapPopup, resourceId, mapPadding, campusFlow, flowBuilding, setFilters])
}
